import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import LanguageService from '@services/LanguageService'
import { BANNER_TRANSLATIONS } from '@constants/bannerTranslations'

/**
 * Normalize browser language code
 *
 * @param {string} browserLang - Raw browser language from navigator.language
 * @returns {string} Normalized language code
 */
function normalizeBrowserLanguage (browserLang) {
  if (!browserLang) return null

  const langCode = browserLang.split('-')[0].toLowerCase()

  if (LanguageService.SUPPORTED_LANGUAGES.includes(langCode)) {
    return langCode
  }

  return null
}

/**
 * Get translations for the banner in a specific language
 *
 * @param {string} langCode - Language code for banner text
 * @returns {Object|null} Translation object or null if not found
 */
function getBannerTranslations (langCode) {
  return BANNER_TRANSLATIONS[langCode] || null
}

/**
 * Custom hook for Language Banner logic
 *
 * Implements the complete decision logic based on the state table:
 * - Determines if banner should be shown
 * - Provides the language for banner content
 * - Explicit dismissal saves language preference (permanent)
 * - Implicit dismissal only persists for session (gives second chance next visit))
 *
 * @returns {Object} Banner state and handlers
 * @property {boolean} showBanner - Whether to display the banner
 * @property {string|null} bannerLang - Language code for banner text (browserLang)
 * @property {string|null} currentLang - Current language from URL
 * @property {Function} handleAccept - Accept handler (navigate + save)
 * @property {Function} handleDismiss - Dismiss handler (save current)
 * @property {Object|null} translations - Banner text translations
 */
export function useLanguageBanner () {
  const navigate = useNavigate()
  const location = useLocation()

  const [showBanner, setShowBanner] = useState(false)
  const [bannerLang, setBannerLang] = useState(null)
  const [currentLang, setCurrentLang] = useState(null)

  // Ref to track banner visibility for cleanup (avoids stale closure)

  const showBannerRef = useRef(false)

  // Keep ref in sync with state
  useEffect(() => {
    showBannerRef.current = showBanner
  }, [showBanner])

  // Implicit dismissal: runs cleanup before next navigation effect
  useEffect(() => {
    return () => {
      // If banner was showing and user navigated, dismiss for session
      if (showBannerRef.current) {
        LanguageService.dismissBannerForSession()
      }
    }
  }, [location.pathname])

  // Main decision logic
  useEffect(() => {
    // Step 1: Get current language from URL
    const urlLang = LanguageService.getCurrentLanguageFromURL()
    setCurrentLang(urlLang)

    // If no language in URL, don't show banner (handled by LanguageDetector)
    if (!urlLang) {
      setShowBanner(false)
      return
    }

    // Step 2: Check if user already made an explicit choice (stored preference)
    const storedLang = LanguageService.getStoredLanguage()
    if (storedLang) {
      setShowBanner(false)
      return
    }

    // Step 3: Check if banner was dismissed implicitly this session
    if (LanguageService.isBannerDismissedForSession()) {
      setShowBanner(false)
      return
    }

    // Step 4: Get and normalize browser language
    const rawBrowserLang = navigator.language || navigator.userLanguage
    const normalizedBrowserLang = normalizeBrowserLanguage(rawBrowserLang)

    // Step 5: If browserLang is not supported, don't show banner
    if (!normalizedBrowserLang) {
      setShowBanner(false)
      return
    }

    // Step 6: If browserLang matches currentLang, don't show banner
    if (normalizedBrowserLang === urlLang) {
      setShowBanner(false)
      return
    }

    // Step 7: Show banner suggesting browserLang
    setBannerLang(normalizedBrowserLang)
    setShowBanner(true)
  }, [location.pathname]) // Re-evaluate when URL changes

  /**
   * Handle Accept: Navigate to suggested language and save preference
   * This is an explicit choice - persists permanently
   */
  const handleAccept = () => {
    if (!bannerLang) return

    // Save the new language preference
    LanguageService.saveLanguage(bannerLang)

    // Navigate to the suggested language
    const currentPath = location.pathname
    const newPath = currentPath.replace(`/${currentLang}`, `/${bannerLang}`)

    navigate(newPath, { replace: true })

    // Hide banner
    setShowBanner(false)
  }

  /**
   * Handle Dismiss: Save current language as preference (don't show again)
   * This is an explicit choice - persists permanently
   */
  const handleDismiss = () => {
    if (!currentLang) return

    // Save current language as preference (permanent)
    LanguageService.saveLanguage(currentLang)

    // Hide banner
    setShowBanner(false)
  }

  // Get translations for banner (in browserLang, not currentLang)
  const translations = bannerLang ? getBannerTranslations(bannerLang) : null

  return {
    showBanner,
    bannerLang,
    currentLang,
    handleAccept,
    handleDismiss,
    translations
  }
}
